import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clearStoredOrganizationScope,
  getStoredOrganizationScope,
  type LegalEntity,
  listLegalEntities,
  listOrganizations,
  type OrganizationResponse,
  type OrganizationScope,
  setStoredOrganizationScope,
} from "@/lib/api";
import { useSession } from "@/lib/session";

interface OrganizationValue {
  scope: OrganizationScope | null;
  /** Console handoff organization, validated as visible to this session. */
  handoffOrganizationId: string | null;
  organizations: OrganizationResponse[];
  legalEntities: LegalEntity[];
  loading: boolean;
  error: string | null;
  /** Re-fetches organizations/entities for the current session. */
  refresh: () => Promise<void>;
  /**
   * Opens another legal entity's books in this organization.
   * The organization stays on the Console handoff.
   * Returns false when the id is missing or already open.
   */
  selectLegalEntity: (legalEntityId: string) => boolean;
}

const OrganizationContext = createContext<OrganizationValue | null>(null);

function describeError(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const [organizations, setOrganizations] = useState<OrganizationResponse[]>(
    [],
  );
  const [legalEntities, setLegalEntities] = useState<LegalEntity[]>([]);
  const [scope, setScope] = useState<OrganizationScope | null>(null);
  const [handoffOrganizationId, setHandoffOrganizationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (state.status !== "authenticated") {
      setOrganizations([]);
      setLegalEntities([]);
      setScope(null);
      setHandoffOrganizationId(null);
      clearStoredOrganizationScope();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const orgs = await listOrganizations();
      setOrganizations(orgs);
      const handoff = getStoredOrganizationScope();
      if (!handoff.organization_id) {
        setLegalEntities([]);
        setScope(null);
        setHandoffOrganizationId(null);
        clearStoredOrganizationScope();
        return;
      }
      const org = orgs.find((candidate) => candidate.id === handoff.organization_id);
      if (!org) {
        setLegalEntities([]);
        setScope(null);
        setHandoffOrganizationId(null);
        clearStoredOrganizationScope();
        return;
      }
      setHandoffOrganizationId(org.id);
      const entities = await listLegalEntities(org.id);
      setLegalEntities(entities);
      const primary = entities.find((entity) => entity.is_primary) ?? entities[0];
      const entityId = entities.some((entity) => entity.id === handoff.legal_entity_id)
        ? (handoff.legal_entity_id as string)
        : (primary?.id ?? "");
      const next: OrganizationScope = {
        organization_id: org.id,
        legal_entity_id: entityId,
      };
      // The organization comes only from the Console handoff. The legal
      // entity may then be switched inside Accounting; that choice stays in
      // memory and in the page address, never in a stored scope.
      if (next.legal_entity_id) {
        setStoredOrganizationScope(next);
        setScope(next);
      } else {
        setScope(null);
        clearStoredOrganizationScope();
      }
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setLoading(false);
    }
  }, [state.status]);

  const selectLegalEntity = useCallback(
    (legalEntityId: string) => {
      if (!scope || scope.legal_entity_id === legalEntityId) return false;
      if (!legalEntities.some((entity) => entity.id === legalEntityId)) {
        return false;
      }
      const next: OrganizationScope = {
        ...scope,
        legal_entity_id: legalEntityId,
      };
      setStoredOrganizationScope(next);
      setScope(next);
      return true;
    },
    [legalEntities, scope],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<OrganizationValue>(
    () => ({
      scope,
      handoffOrganizationId,
      organizations,
      legalEntities,
      loading,
      error,
      refresh,
      selectLegalEntity,
    }),
    [
      scope,
      handoffOrganizationId,
      organizations,
      legalEntities,
      loading,
      error,
      refresh,
      selectLegalEntity,
    ],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization(): OrganizationValue {
  const value = useContext(OrganizationContext);
  if (!value)
    throw new Error("useOrganization() must be used inside OrganizationProvider");
  return value;
}
